import { format, isValid, parseISO } from 'date-fns';
import { enIN } from 'date-fns/locale';
import setting from './config.util';

const formatDate = (value: Date | number | string, format: string): string => {
  let date: Date | null = null;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "number") {
    date = new Date(value);
  } else if (typeof value === "string") {
    const s = value.trim();
    if (/^\d+$/.test(s)) {
      const n = Number(s);
      date = new Date(n);
    }
    if (!date) {
      const parsed = new Date(s);
      if (!isNaN(parsed.getTime())) {
        date = parsed;
      }
    }

    if (!date && /^(\d{1,2}):(\d{2})$/.test(s)) {
      const [, hh, mm] = s.match(/^(\d{1,2}):(\d{2})$/)!;
      const now = new Date();
      now.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
      date = now;
    }

    if (!date && /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/.test(s)) {
      const tryDate = new Date(s);
      if (!isNaN(tryDate.getTime())) {
        date = tryDate;
      } else {
        const parts = s.split(/[-\/]/).map((p) => parseInt(p, 10));
        if (parts.length === 3) {
          let d = parts[0],
            m = parts[1],
            y = parts[2];
          if (y < 100) y += y < 50 ? 2000 : 1900;
          date = new Date(y, m - 1, d);
        }
      }
    }

    if (!date) return "";
  } else {
    return "";
  }

  if (!date || isNaN(date.getTime())) return "";

  const fromNow = (): string => {
    const now = new Date();
    const diff = now.getTime() - date!.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    if (months > 0) return `${months} months ago`;
    if (days > 0) return `${days} days ago`;
    if (hours > 0) return `${hours} hours ago`;
    if (minutes > 0) return `${minutes} minutes ago`;
    return "just now";
  };

  const getWeek = (): string => {
    const d = new Date(date!.getTime());
    const firstJan = new Date(d.getFullYear(), 0, 1);
    const days = Math.floor((d.getTime() - firstJan.getTime()) / 86400000);
    const week = Math.ceil((days + firstJan.getDay() + 1) / 7);
    return String(week);
  };

  const formatTime = (): string =>
    date!.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const monthShortName = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  const timestamp = date.getTime();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const weekdayShort = date.toLocaleString("default", { weekday: "short" });
  const hours12 = hours % 12 || 12;
  const indicator = hours < 12 ? "AM" : "PM";
  const tokens: Record<string, string> = {
    yyyy: String(year),
    mm: String(month).padStart(2, "0"),
    dd: String(day).padStart(2, "0"),
    h: String(hours12).padStart(2, "0"),
    m: String(minutes).padStart(2, "0"),
    mmm: monthShortName,
    a: indicator,
    ts: String(timestamp),
    fromNow: fromNow(),
    week: getWeek(),
    formatTime: formatTime(),
    day: weekdayShort,
  };
  const tokenKeys = Object.keys(tokens).sort((a, b) => b.length - a.length);
  const tokenRegex = new RegExp("(" + tokenKeys.join("|") + ")", "g");

  return format?.replace(tokenRegex, (match) => tokens[match] ?? match);
};

const formatEventDate = (eventDate: string | number | null) => {
  let date: Date | null = null;

  if (
    typeof eventDate === 'number' ||
    (!isNaN(Number(eventDate)) && eventDate !== '')
  ) {
    date = new Date(Number(eventDate));
  } else if (typeof eventDate === 'string') {
    const parsed = parseISO(eventDate);
    date = isValid(parsed) ? parsed : new Date(eventDate);
  }

  if (!date || !isValid(date)) {
    return String(eventDate);
  }
  return format(date, 'EEE d LLL yyyy', { locale: enIN });
};

type EventDateInput = {
  isRecurringEvent?: boolean;
  isDateLong?: boolean;
  dateString?: string;
  eventDate?: number | string | null;
  startDate?: number | string | null;
};

// Single source of truth for the event date label. Formats in IST
// (Asia/Kolkata) so the SERVER (page header) and the CLIENT (specs card) always
// show the SAME date — otherwise the server (UTC) can render the previous day.
const getEventDateLabel = (data: EventDateInput): string => {
  if (data?.isRecurringEvent || data?.isDateLong) return data?.dateString || '';

  const raw = data?.eventDate ?? data?.startDate;
  if (raw === undefined || raw === null || raw === '') {
    return data?.dateString || '';
  }

  const date = new Date(isNaN(Number(raw)) ? String(raw) : Number(raw));
  if (!isValid(date)) return data?.dateString || '';

  // e.g. "Fri 26 Jun 2026" (matches the previous 'EEE d LLL yyyy' style)
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  })
    .format(date)
    .replace(/,/g, '');
};

const replaceImage = (url: string): string => {
  try {
    const parsedUrl = new URL(url);

    const isS3 = parsedUrl?.hostname.includes("s3");
    const isHttps = parsedUrl?.protocol === "https:";
    if (!isS3 || !isHttps) return url;

    if (isS3 && isHttps) {
      let newUrl = `${setting?.IMAGE_URL}${parsedUrl.pathname}`;
      if (parsedUrl?.search) newUrl += parsedUrl.search;
      if (newUrl?.endsWith("/")) {
        newUrl = newUrl?.slice(0, -1);
      }
      return newUrl || "";
    }

    return parsedUrl.toString();
  } catch {
    return url;
  }
};

const isEmpty = (value: any) => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === "string" || Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    return Object.keys(value).length === 0;
  }

  if (typeof value === "number" && value <= 0) {
    return true;
  }

  return false;
};

export {
  formatDate,
  formatEventDate,
  getEventDateLabel,
  replaceImage,
  isEmpty,
};
